import { memo, useCallback } from 'react';

import { useMarketDataCard } from '@src/components/MarketDataCard/useMarketDataCard';
import { ExpressedOutcomeEnum, SideEnum, Text, useSecurity } from '@talos/kyoko';

import { PredictionChanceMeter } from './PredictionChanceMeter';
import type { PredictionOutcomePriceSummary } from './PredictionOutcomePriceSubscriber';
import {
  formatPredictionDecimalOutcomeButtonLabel,
  formatPredictionOddsLabel,
  getPredictionPriceIncrement,
} from './predictionDisplay';
import { getPredictionOutcomePrice } from './predictionMath';
import {
  OutcomeButtonRow,
  OutcomeOdds,
  OutcomePriceButton,
  OutcomeRFQButton,
  OutcomeRow,
  OutcomeRowHeader,
  SingleOutcomeButtonRow,
  SingleOutcomeTradePanel,
} from './styles';
import { usePrimePredictionOrder } from './usePrimePredictionOrder';
import { usePrimePredictionRFQ } from './usePrimePredictionRFQ';

interface PredictionOutcomeRowProps {
  symbol: string;
  label: string;
  compact: boolean;
  /** When false, header already shows the outcome — body is just Yes/No controls. */
  showLabel?: boolean;
  /** Whether the venue the card would prime an RFQ against supports RFQ at all. */
  isRfqSupported: boolean;
  marketAccounts?: string[];
  aggregation?: string;
  orderPresetID?: string;
  quantity?: string;
  subAccount?: string;
  prices?: PredictionOutcomePriceSummary;
}

export const PredictionOutcomeRow = memo(function PredictionOutcomeRow({
  symbol,
  label,
  compact: isCompact,
  showLabel = true,
  isRfqSupported,
  marketAccounts,
  aggregation,
  orderPresetID,
  quantity,
  subAccount,
  prices,
}: PredictionOutcomeRowProps) {
  const primePredictionOrder = usePrimePredictionOrder();
  const primePredictionRFQ = usePrimePredictionRFQ();
  const security = useSecurity(symbol);
  const { canRequestRFQ } = useMarketDataCard(security);
  const priceIncrement = getPredictionPriceIncrement(security);

  const chancePct = prices?.chancePct;
  const yesAskPrice = prices?.yesAskPrice;
  const yesBidPrice = prices?.yesBidPrice;

  // Yes/No buttons always show the executable buy side (Yes ask / No ask = 1 - Yes bid), never mid.
  const yesPrice = getPredictionOutcomePrice(ExpressedOutcomeEnum.Yes, SideEnum.Buy, {
    yesAsk: yesAskPrice,
    yesBid: yesBidPrice,
  });
  const noPrice = getPredictionOutcomePrice(ExpressedOutcomeEnum.No, SideEnum.Buy, {
    yesAsk: yesAskPrice,
    yesBid: yesBidPrice,
  });
  const yesLabel = formatPredictionDecimalOutcomeButtonLabel(ExpressedOutcomeEnum.Yes, yesPrice, priceIncrement);
  const noLabel = formatPredictionDecimalOutcomeButtonLabel(ExpressedOutcomeEnum.No, noPrice, priceIncrement);
  const chanceLabel = formatPredictionOddsLabel(chancePct);
  const isSingleOutcome = !showLabel;

  const handleYesClick = useCallback(() => {
    primePredictionOrder({
      symbol,
      outcome: ExpressedOutcomeEnum.Yes,
      marketAccounts,
      aggregation,
      orderPresetID,
      orderQty: quantity,
      subAccount,
      price: yesPrice,
    });
  }, [aggregation, marketAccounts, orderPresetID, primePredictionOrder, quantity, subAccount, symbol, yesPrice]);

  const handleNoClick = useCallback(() => {
    primePredictionOrder({
      symbol,
      outcome: ExpressedOutcomeEnum.No,
      marketAccounts,
      aggregation,
      orderPresetID,
      orderQty: quantity,
      subAccount,
      price: noPrice,
    });
  }, [aggregation, marketAccounts, orderPresetID, primePredictionOrder, quantity, subAccount, symbol, noPrice]);

  const handleRFQClick = useCallback(() => {
    primePredictionRFQ({
      symbol,
      orderQty: quantity,
      marketAccounts,
      aggregation,
      subAccount,
    });
  }, [aggregation, marketAccounts, primePredictionRFQ, quantity, subAccount, symbol]);

  return (
    <OutcomeRow $compact={isCompact} $singleOutcome={isSingleOutcome} data-testid={`prediction-outcome-row-${symbol}`}>
      {showLabel ? (
        <OutcomeRowHeader>
          <Text size={isCompact ? 'fontSizeTiny' : 'fontSizeSmall'} lineClamp={1} flex={1}>
            {label}
          </Text>
          <OutcomeOdds>
            <Text color="colorTextDefault" size={isCompact ? 'fontSizeTiny' : 'fontSizeSmall'}>
              {chanceLabel}
            </Text>
          </OutcomeOdds>
        </OutcomeRowHeader>
      ) : null}
      {isSingleOutcome ? (
        <SingleOutcomeTradePanel>
          <SingleOutcomeButtonRow>
            <OutcomePriceButton
              $variant="yes"
              $compact={isCompact}
              onClick={handleYesClick}
              data-testid={`prediction-outcome-yes-${symbol}`}
            >
              {yesLabel}
            </OutcomePriceButton>
            <OutcomeRFQButton
              $compact={isCompact}
              disabled={!isRfqSupported || !canRequestRFQ}
              onClick={handleRFQClick}
              data-testid={`prediction-outcome-rfq-${symbol}`}
            >
              RFQ
            </OutcomeRFQButton>
            <OutcomePriceButton
              $variant="no"
              $compact={isCompact}
              onClick={handleNoClick}
              data-testid={`prediction-outcome-no-${symbol}`}
            >
              {noLabel}
            </OutcomePriceButton>
          </SingleOutcomeButtonRow>
          <PredictionChanceMeter chancePct={chancePct} />
        </SingleOutcomeTradePanel>
      ) : (
        <OutcomeButtonRow>
          <OutcomePriceButton
            $variant="yes"
            $compact={isCompact}
            onClick={handleYesClick}
            data-testid={`prediction-outcome-yes-${symbol}`}
          >
            {yesLabel}
          </OutcomePriceButton>
          <OutcomeRFQButton
            $compact={isCompact}
            disabled={!isRfqSupported || !canRequestRFQ}
            onClick={handleRFQClick}
            data-testid={`prediction-outcome-rfq-${symbol}`}
          >
            RFQ
          </OutcomeRFQButton>
          <OutcomePriceButton
            $variant="no"
            $compact={isCompact}
            onClick={handleNoClick}
            data-testid={`prediction-outcome-no-${symbol}`}
          >
            {noLabel}
          </OutcomePriceButton>
        </OutcomeButtonRow>
      )}
    </OutcomeRow>
  );
});
